import React from 'react';
import { Link } from 'react-router';
import PublicNav from './PublicNav';
import PublicFooter from './PublicFooter';
import ScreenshotFrame from './ScreenshotFrame';
import usePageMeta from './usePageMeta';
import { STEPS, PILOT_PATH, PILOT_GUARANTEE } from './publicContent';
import { C, container, sectionH2, sectionSub, primaryBtnStyle } from './publicStyles';

import heroShot from '../../assets/public/hero-ranked-candidates.png';

const HERO_SHOT = heroShot;

const INDUSTRIES = ['Batteries and energy storage', 'Catalysts', 'Semiconductors', 'Coatings and ceramics', 'Photovoltaics', 'Aerospace and defense'];

// The cost the buyer already pays, stated plainly. No invented numbers.
const PAIN = [
  { title: 'Every wrong candidate costs lab time', desc: 'Synthesis and characterization are the expensive steps. A shortlist that looks good on paper and fails at the bench burns weeks of your team.' },
  { title: 'The tools do not talk to each other', desc: 'One generative model, a separate structure toolkit, a DFT code on someone else\'s cluster, then a report written by hand. Your computational people spend their week gluing it together.' },
  { title: 'A single model is confident outside what it knows', desc: 'Published benchmarks show property predictions degrade outside a model\'s training chemistry. Without a check, you only find out at the bench.' },
];

const OUTCOMES = [
  { title: 'A ranked shortlist from a plain-language target', desc: 'Inorganic crystals and small molecules, scored and ranked, with a 3D viewer for every structure.' },
  { title: 'Checked before anyone touches a furnace', desc: 'DFT validation (GPAW and Quantum ESPRESSO) and a novelty check against Materials Project, built into the same run.' },
  { title: 'A report your team and patent counsel can use', desc: 'Structure, properties, comparisons against known materials and economics in one exportable document.' },
  { title: 'Nothing to install', desc: 'Runs on managed cloud GPUs. Every run is saved to your organization\'s searchable library.' },
];

const FAQ = [
  {
    q: 'Our chemistry is unusual. Will this work on it?',
    a: 'That is what the pilot is for. Before day one we agree in writing on a target from your own program and a success test. You see DFT results and novelty checks for each candidate, not just a model score, so weak candidates show up as weak.',
  },
  {
    q: 'We already have a computational team and DFT licenses.',
    a: 'Spectra gives that team a generation and screening front end and runs validation on managed GPUs, so their time goes to judgment instead of plumbing. Structures export as standard CIF files for your own tools.',
  },
  {
    q: 'What is actually yours, and what is open source?',
    a: 'The generative models are leading published ones: MatterGen for crystals, IBM FM4M and GT4SD for molecules. Spectra\'s planner, built in-house, turns your target into a formal spec and picks and tunes the right model for it deterministically, then ranks, validates and reports. You can also choose models by hand.',
  },
  {
    q: 'How is our data handled?',
    a: 'Account data and queries are never sold. Enterprise agreements can exclude your data from pooling entirely, with data residency and run-in-your-own-cloud options.',
  },
  {
    q: 'Who is this not for?',
    a: 'Spectra is built for funded industrial R&D teams with a live materials program. It is not a fit for a single researcher trying out generative models, or for coursework.',
  },
];

function Pilot({ dense }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <Link to={PILOT_PATH} style={primaryBtnStyle}>Request a pilot</Link>
      <p style={{ color: C.muted, fontSize: 13, margin: 0, maxWidth: dense ? 520 : 460, lineHeight: 1.6 }}>
        {PILOT_GUARANTEE}
      </p>
    </div>
  );
}

export default function PublicLanding() {
  usePageMeta({
    title: 'AI Materials Discovery for Industrial R&D | CrimsonScan',
    description: 'Generate, rank and DFT-validate new crystals and molecules for batteries, catalysts, semiconductors and coatings, with patent-grade reports.',
    path: '/',
  });

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column' }}>
      <PublicNav />

      <main>
        {/* Hero: one buyer, one promise, one call to action */}
        <header style={{ ...container, textAlign: 'center', padding: '84px 24px 48px' }}>
          <p style={{ color: C.crimson, fontWeight: 700, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 16px' }}>
            AI materials discovery for industrial R&D
          </p>
          <h1 style={{ color: C.text, fontWeight: 800, fontSize: 'clamp(1.9rem, 4.5vw, 3rem)', lineHeight: 1.15, marginBottom: 18, maxWidth: 820, marginLeft: 'auto', marginRight: 'auto' }}>
            Find the materials worth making{' '}
            <span style={{ color: C.crimson }}>before you pay to make them.</span>
          </h1>
          <p style={{ ...sectionSub, margin: '0 auto 32px', fontSize: 17 }}>
            Describe the material you need in plain language. Spectra generates, ranks and
            DFT-validates crystal and molecule candidates, then hands your team a
            patent-grade report. No installs, no pipeline to maintain.
          </p>
          <Pilot />
        </header>

        {/* Hero screenshot */}
        <section style={{ ...container, paddingBottom: 40 }}>
          <ScreenshotFrame
            src={HERO_SHOT}
            alt="Spectra results table showing 16 generated crystal candidates ranked by structural quality score"
            caption="Ranked candidates from a single generation run"
          />
        </section>

        {/* Who it is for */}
        <section style={{ ...container, paddingBottom: 72 }}>
          <p style={{ color: C.muted, fontSize: 13, textAlign: 'center', margin: '0 0 12px' }}>Built for materials R&D teams in</p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
            {INDUSTRIES.map(i => (
              <li key={i} style={{ color: C.body, fontSize: 13, border: `1px solid ${C.border}`, borderRadius: 999, padding: '6px 14px' }}>{i}</li>
            ))}
          </ul>
        </section>

        {/* The pain */}
        <section style={{ background: C.panel, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ ...container, padding: '64px 24px' }}>
            <h2 style={{ ...sectionH2, textAlign: 'center' }}>The expensive part of discovery is the wrong candidate</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 22, marginTop: 28 }}>
              {PAIN.map(p => (
                <div key={p.title}>
                  <h3 style={{ color: C.text, fontWeight: 700, fontSize: '1.05rem', marginBottom: 8 }}>{p.title}</h3>
                  <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, margin: 0 }}>{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Three steps */}
        <section style={{ ...container, padding: '72px 24px' }}>
          <h2 style={{ ...sectionH2, textAlign: 'center' }}>How it works</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18, marginTop: 28 }}>
            {STEPS.map(s => (
              <div key={s.n} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: '26px 24px' }}>
                <div style={{ color: C.crimson, fontWeight: 800, fontSize: 13, letterSpacing: 2, marginBottom: 10 }}>
                  STEP {s.n}
                </div>
                <h3 style={{ color: C.text, fontWeight: 700, fontSize: '1.1rem', marginBottom: 8 }}>{s.title}</h3>
                <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.65, margin: 0 }}>{s.blurb}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Link to="/how-it-works" style={{ color: C.info, fontSize: 14, textDecoration: 'none' }}>
              See the full walkthrough →
            </Link>
          </div>
        </section>

        {/* What your team gets */}
        <section style={{ background: C.panel, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ ...container, padding: '64px 24px' }}>
            <h2 style={{ ...sectionH2, textAlign: 'center' }}>What your team gets</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24, marginTop: 28 }}>
              {OUTCOMES.map(o => (
                <div key={o.title}>
                  <h3 style={{ color: C.green, fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{o.title}</h3>
                  <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, margin: 0 }}>{o.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Proof we can show today: the product itself */}
        <section style={{ ...container, padding: '72px 24px', textAlign: 'center' }}>
          <h2 style={sectionH2}>Check the work before you talk to us</h2>
          <p style={{ ...sectionSub, margin: '0 auto 24px' }}>
            Read a full sample report, and the published performance numbers for the
            models Spectra runs, with links to the papers.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/sample-report.pdf" target="_blank" rel="noreferrer" style={{ color: C.info, fontSize: 15, textDecoration: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 22px' }}>
              Sample report (PDF)
            </a>
            <Link to="/docs/benchmarks" style={{ color: C.info, fontSize: 15, textDecoration: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 22px' }}>
              Model benchmarks
            </Link>
          </div>
        </section>

        {/* The pilot offer */}
        <section style={{ ...container, paddingBottom: 72 }}>
          <div style={{ background: C.panel, border: `1px solid ${C.crimson}`, borderRadius: 14, padding: '40px 32px', maxWidth: 820, margin: '0 auto', textAlign: 'center' }}>
            <h2 style={sectionH2}>Prove it on your own program in three months</h2>
            <p style={{ ...sectionSub, margin: '0 auto 24px' }}>
              A paid pilot, hosted by us, with your team on the full platform. You bring a target
              from a live project; we agree the success test in writing before day one.
            </p>
            <Pilot dense />
          </div>
        </section>

        {/* Objections */}
        <section style={{ ...container, paddingBottom: 72 }}>
          <h2 style={{ ...sectionH2, textAlign: 'center', marginBottom: 28 }}>Common questions</h2>
          <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {FAQ.map(f => (
              <div key={f.q} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '20px 24px' }}>
                <h3 style={{ color: C.text, fontWeight: 700, fontSize: 15, marginBottom: 8 }}>{f.q}</h3>
                <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, margin: 0 }}>{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Close */}
        <section style={{ ...container, textAlign: 'center', padding: '24px 24px 80px' }}>
          <h2 style={sectionH2}>Spend bench time only on candidates that earned it</h2>
          <p style={{ ...sectionSub, margin: '0 auto 28px' }}>
            Tell us the material you need. We reply within one business day with pilot terms.
          </p>
          <Pilot />
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
